import { useState } from 'react';
import type { Conversation } from '@arcnet/contracts';
import { useConversation, useConversations } from '../../lib/api/hooks-conversations.js';
import { Badge, CardHead, Empty, ErrorBanner, SectionHead, Tab, TableSkeleton, Tabs, SkeletonBlock } from '../../components/ui/Primitives.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Every WhatsApp and Telegram conversation on the account, for a supervisor (docs/43 §5.3):
 * what is waiting and for how long, who has what, and what was closed with which outcome. Read
 * only: agents answer them in the workspace.
 */

const CHANNEL_LABEL: Record<Conversation['kind'], string> = { whatsapp: 'WhatsApp', telegram: 'Telegram' };
const who = (c: Conversation): string => c.customer_name ?? (c.customer_number ? `+${c.customer_number}` : 'Customer');

function State({ c }: { c: Conversation }) {
  if (c.state === 'queued') return <Badge tone="warn">waiting</Badge>;
  if (c.state === 'offered') return <Badge tone="info">ringing{c.agent_name ? ` at ${c.agent_name}` : ''}</Badge>;
  if (c.state === 'active') return <Badge tone="ok">with {c.agent_name ?? 'an agent'}</Badge>;
  return <Badge tone="muted">{c.outcome ?? 'closed'}</Badge>;
}

export function ConversationsTab({ tenantId }: { tenantId: string }) {
  const [state, setState] = useState<'open' | 'closed'>('open');
  const list = useConversations(tenantId, 'all', state);
  const [open, setOpen] = useState<string | null>(null);
  const rows = list.data?.data ?? [];
  return (
    <>
      <SectionHead title="Conversations" sub="WhatsApp and Telegram, waiting, with agents, and closed." />
      <div className="arc-card">
        <Tabs style={{ padding: '0 14px' }}>
          <Tab active={state === 'open'} onSelect={() => { setState('open'); setOpen(null); }}>Open</Tab>
          <Tab active={state === 'closed'} onSelect={() => { setState('closed'); setOpen(null); }}>Closed</Tab>
        </Tabs>
        {list.error && <div className="arc-card-body"><ErrorBanner error={list.error} /></div>}
        {list.data === undefined ? <table className="arc-table"><TableSkeleton query={list} cols={5} rows={3} /></table>
          : rows.length === 0 ? (
            <Empty icon="message" title={state === 'open' ? 'Nothing open' : 'None closed yet'}
              {...(state === 'open' ? { hint: 'Conversations appear here as customers write to your WhatsApp numbers and Telegram bots.' } : {})} />
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Customer</th><th>Channel</th><th>Queue</th><th>State</th>
                  <th>{state === 'open' ? 'Waiting since' : 'Closed'}</th></tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} onClick={() => setOpen(open === c.id ? null : c.id)} style={{ cursor: 'pointer' }}
                      aria-selected={open === c.id}>
                      <td>
                        <div style={{ fontWeight: 550 }}>{who(c)}</div>
                        <div className="arc-subtle arc-truncate" style={{ fontSize: 12, maxWidth: 320 }}>{c.preview ?? ''}</div>
                      </td>
                      <td>{CHANNEL_LABEL[c.kind]}<div className="arc-subtle" style={{ fontSize: 12 }}>{c.connection_name}</div></td>
                      <td>{c.queue_name ?? <span className="arc-subtle">none</span>}</td>
                      <td><State c={c} /></td>
                      <td className="arc-subtle">{relativeTime(state === 'open' ? c.queued_at : (c.closed_at ?? c.last_message_at))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {open && <ReadThread tenantId={tenantId} id={open} />}
    </>
  );
}

function ReadThread({ tenantId, id }: { tenantId: string; id: string }) {
  const q = useConversation(tenantId, id);
  const c = q.data?.conversation;
  return (
    <div className="arc-card" style={{ marginTop: 16 }}>
      <CardHead title={c ? who(c) : 'Conversation'} sub={c ? `${CHANNEL_LABEL[c.kind]}${c.note ? ` · ${c.note}` : ''}` : undefined} />
      {q.error && <div className="arc-card-body"><ErrorBanner error={q.error} /></div>}
      {!q.data ? <div className="arc-card-body"><SkeletonBlock height={120} query={q} /></div> : (
        <div className="arc-ws-msg-body" style={{ maxHeight: 420 }}>
          {q.data.messages.map((m) => (
            <div key={m.id} className="arc-ws-msg" data-dir={m.direction}>
              <div className="arc-ws-msg-bubble">
                {m.attachments.map((a, i) => <div key={i} className="arc-subtle">[{a.type}]</div>)}
                {m.body}
              </div>
              <div className="arc-ws-msg-meta arc-subtle">
                {new Date(m.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                {m.author ? ` · ${m.author}` : ''}{m.status === 'failed' ? ' · not sent' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
